import React, { useEffect } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import { Chips } from 'components/atoms/Chips';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Link, useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { IDictionary } from 'models/dictionary';
import { IAmmunition, IAncillary } from 'models/ancillaries';
import { findInDictionary, parseDictionary } from 'helpers/dictionary';
import { LEGAL_ID } from 'pages/Firearms/RegisteredFirearmsMapPage/FirearmsLocationMap';
import { renderDataColorByValues } from 'components/molecules/Table/helpers';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';

interface IAncillaryDataListItem {
  data: IAmmunition | IAncillary;
  state: IDictionary[] | null;
}

const AmmunitionDataListItem = ({ data, state }: IAncillaryDataListItem) => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { hasPermissions } = usePermission();
  const isLegal = data.legalityUniqueId === LEGAL_ID;
  const { selectedLanguage } = useLang();
  const {
    getArtAmmunitionType,
    artAmmunitionType,
    getArtAmmunitionCalibreGauge,
    artAmmunitionCalibreGauge,
    getArtAmmunitionManufacturer,
    artAmmunitionManufacturer,
  } = useDictionary();

  useEffect(() => {
    (async function initDictionaries() {
      getArtAmmunitionType();
      getArtAmmunitionCalibreGauge();
      getArtAmmunitionManufacturer();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.gray200}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
        backgroundColor: `${isLegal ? colors.gray100 : colors.error15}`,
      }}
    >
      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.firecycleId.label')}
        </Typography>
        <Typography variant="body2">{data.fireCycleId}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.lotNumber.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.lotNumber)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.batchNumber.label')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.batchNumber)}</Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.ammunitionType.label')}
        </Typography>
        <Typography variant="body2">
          {findInDictionary(data.typeUniqueId, artAmmunitionType)}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.calibre.label')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(
            findInDictionary(data.calibreGaugeUniqueId, artAmmunitionCalibreGauge),
          )}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'ancillaries.ancillariesLocation.ammunitionDetails.section.ammunitionManufacturer.label',
          )}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(
            findInDictionary(data.manufacturerUniqueId, artAmmunitionManufacturer),
          )}
        </Typography>
      </Grid>

      <Grid size={{ xs: 6 }} sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('ancillaries.ancillariesLocation.ammunitionDetails.section.state.label')}
        </Typography>
        <Chips
          text={parseDictionary(state, 'name')[data.stateUniqueId]}
          variant={
            renderDataColorByValues(
              data.activityUniqueId,
              data.legalityUniqueId,
              data.stateUniqueId,
            ).stateField
          }
        />
      </Grid>

      {hasPermissions(
        [EPermission.VIEW_LIMITED_ANCILLARIES, EPermission.VIEW_ALL_ANCILLARIES],
        true,
      ) && (
        <Grid
          size={{ xs: 12 }}
          sx={{
            margin: '0 10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderTop: {
              xs: `1px solid ${colors.gray200}`,
            },
            p: 2,
          }}
        >
          <Grid size={{ xs: 12 }}>
            <Link
              to={ERouteLinks.ViewAmmunition.replace(':id', data.uniqueId)}
              target="_blank"
            >
              <Button
                label={t('registeredFirearms.firearmsLocation.seeDetails.label')}
                variant={EButtonVariants.outlined}
                size="small"
                permissionOr
                permission={[
                  EPermission.VIEW_LIMITED_ANCILLARIES,
                  EPermission.VIEW_ALL_ANCILLARIES,
                ]}
                fullWidth
              />
            </Link>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default AmmunitionDataListItem;
